import { API } from '../../api';
import { Button, Input, List, Space, notification } from 'antd';
import { getGroovyFuncs, getTemplateClientId, handleError } from '../../utils/util';
import { v4 as uuidv4 } from 'uuid';
import GroovyFunctionOption from './groovyFunctionOption';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

/**
 * Import a single groovy function into the given client.
 * @param {String} clientId
 * @param {String} groovyCode
 * @param {Function} getData
 */
async function importGroovyFuncOnServer(clientId, groovyCode) {
  const fncId = uuidv4();
  const params = {
    id: fncId,
    value: groovyCode,
    client_id: clientId,
    value_type: 'def',
  };

  return API.post(`/stdlib/${fncId}`, params);
}

/**
 * Import a list of groovy functions
 * TODO: Use Promise.all and better handle errors
 * @param {String} clientId
 * @param {Array} groovyFunctions
 * @param {Function} getData
 * @returns
 */
async function importGroovyFuncs(clientId, groovyFunctions, getData, closeModal) {
  let res;

  for (const groovyFunction of groovyFunctions) {
    res = await importGroovyFuncOnServer(clientId, groovyFunction.value);

    if (res.status !== 200) {
      handleError(res);
      return;
    }
  }

  notification.success({
    message: 'Updated (200)',
  });

  await getData();

  closeModal();
}

const ManageGroovyFuncs = props => {
  const { clientId, closeModal, getData, groovyFunctions, mode } = props;

  const [selectedFunctions, setSelectedFunctions] = useState([]);

  const [sharedGroovyFunctions, setSharedGroovyFunctions] = useState([]);

  const [visibleOptions, setVisibleOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const templateClientId = await getTemplateClientId();
      const sharedGroovyFuncs = await getGroovyFuncs({ client_id: templateClientId });
      setSharedGroovyFunctions(sharedGroovyFuncs);
      setVisibleOptions(sharedGroovyFuncs);
    };

    fetchData();
  }, []);

  const groovyFunctionValues = new Set(groovyFunctions.map(d => d.value));

  /**
   * Set the groovy functions visible to the user based on what is in the search input.
   * Called when the search input has been changed.
   * @param {Event} e
   */
  const updateVisibleOptions = e => {
    const visibleFunctions = sharedGroovyFunctions.filter(item =>
      item.value.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setVisibleOptions(visibleFunctions);
  };

  /**
   * Update the selected functions with info on selected or de-selected
   * function.
   * @param {String} groovyFunction
   * @param {Boolean} checked
   */
  const onOptionChange = (groovyFunction, checked) => {
    if (checked) {
      setSelectedFunctions([...selectedFunctions, groovyFunction]);
    } else {
      setSelectedFunctions(selectedFunctions.filter(d => d !== groovyFunction));
    }
  };

  const importButtonContent = (
    <Button
      id="import-groovy-btn"
      type="primary"
      onClick={() => importGroovyFuncs(clientId, selectedFunctions, getData, closeModal)}
      disabled={selectedFunctions.length === 0}
    >
      Import
    </Button>
  );

  return (
    <>
      <p>
        Select the functions you would like to import into the client function library. Functions in
        the list are disabled if the function is already defined within the client. In this case,
        either remove or rename the existing function.
      </p>
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Input
          data-cy="search-groovy-funcs-input-import"
          addonBefore="Search:"
          onChange={e => updateVisibleOptions(e)}
          style={{ width: '350px' }}
        />
        <List
          dataSource={visibleOptions}
          renderItem={item => (
            <GroovyFunctionOption
              disabled={groovyFunctionValues.has(item.value)}
              groovyFunction={item}
              mode={mode}
              onOptionChange={onOptionChange}
            />
          )}
          pagination={{ pageSize: 10, size: 'small' }}
        />
        {importButtonContent}
      </Space>
    </>
  );
};

ManageGroovyFuncs.propTypes = {
  clientId: PropTypes.string,
  closeModal: PropTypes.func,
  getData: PropTypes.func,
  groovyFunctions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  mode: PropTypes.string.isRequired,
};

export default ManageGroovyFuncs;
